import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import BlockIcon from '@material-ui/icons/Block';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import InfoIcon from '@material-ui/icons/Info';
import { apiATC } from '../../config';
import { getClients } from '../../api/clients';
import { analyseImport, resolveImport } from '../../api/importClient';

// ─── Constantes ───────────────────────────────────────────────────────────────

const STEPS = ['Configuration', 'Mapping colonnes', 'Analyse & Résolution', 'Résultat'];

const FIELDS_VENTE = [
  {
    key: 'id_fact',
    label: 'N° Facture / N° Pièce',
    required: true,
    keywords: ['n° piece', 'n°piece', 'num piece', 'numero piece', 'npiece', 'n° facture', 'n°facture', 'num facture', 'numero facture', 'facture', 'piece'],
  },
  {
    key: 'ref_fournisseur',
    label: 'Référence fournisseur',
    required: true,
    keywords: ['référence fournisseur', 'reference fournisseur', 'ref fournisseur', 'ref. fournisseur', 'ref four'],
  },
  {
    key: 'date',
    label: 'Date de vente',
    required: true,
    keywords: ['date vente', 'date de vente', 'date vente client', 'date'],
  },
  {
    key: 'date_piece',
    label: 'Date pièce',
    required: true,
    keywords: ['date piece', 'date pièce', 'date de piece', 'date de pièce', 'piece date'],
  },
  {
    key: 'qte',
    label: 'Quantité',
    required: false,
    keywords: ['quantité', 'quantite', 'qte', 'qty', 'quantity'],
  },
  {
    key: 'prix_vente',
    label: 'Prix de vente',
    required: false,
    keywords: ['prix de vente', 'prix vente', 'prix ht', 'tarif ht', 'pv ht', 'prix'],
  },
  {
    key: 'libelle',
    label: 'Libellé',
    required: false,
    keywords: ['libellé', 'libelle', 'désignation', 'designation', 'description'],
  },
];

const FIELDS_STOCK = [
  {
    key: 'ref_fournisseur',
    label: 'Référence fournisseur',
    required: true,
    keywords: ['référence fournisseur', 'reference fournisseur', 'ref fournisseur', 'ref. fournisseur', 'ref four'],
  },
  {
    key: 'date',
    label: 'Date inventaire',
    required: true,
    keywords: ['date inventaire', 'date', 'date stock'],
  },
  {
    key: 'qte',
    label: 'Quantité stock',
    required: false,
    keywords: ['quantité', 'quantite', 'qte', 'qty', 'stock', 'quantity'],
  },
  {
    key: 'prix_vente',
    label: 'Prix catalogue',
    required: false,
    keywords: ['prix catalogue', 'prix', 'tarif', 'pv'],
  },
  {
    key: 'libelle',
    label: 'Libellé',
    required: false,
    keywords: ['libellé', 'libelle', 'désignation', 'designation', 'description'],
  },
];

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1200,
    margin: '0 auto',
  },
  stepper: {
    marginBottom: theme.spacing(3),
  },
  dropZone: {
    border: '2px dashed #1a73e8',
    borderRadius: 8,
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    color: '#5f6368',
    transition: 'background .2s',
    '&:hover': { background: '#f0f6ff' },
    '&.active': { background: '#e8f0fe', borderColor: '#1557b0' },
  },
  chip: { margin: theme.spacing(0.5) },
  chipSuccess: { backgroundColor: '#34a853', color: '#fff' },
  chipWarning: { backgroundColor: '#fbbc04', color: '#333' },
  chipError: { backgroundColor: '#ea4335', color: '#fff' },
  chipInfo: { backgroundColor: '#4285f4', color: '#fff' },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  ambiguityCard: {
    border: '1px solid #e0c97e',
    borderRadius: 8,
    marginBottom: theme.spacing(1.5),
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    background: '#fff',
  },
  ambiguityCardResolved: {
    border: '1px solid #34a853',
    boxShadow: '0 1px 3px rgba(52,168,83,0.15)',
  },
  ambiguityHeader: {
    background: '#fffde7',
    borderBottom: '1px solid #e0c97e',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: '0 24px',
  },
  ambiguityHeaderResolved: {
    background: '#f1f8f4',
    borderBottom: '1px solid #c6e8cf',
  },
  ambiguityLibelle: {
    fontStyle: 'italic',
    color: '#3c4043',
    fontSize: '0.82rem',
    marginTop: 2,
  },
  ambiguityBody: {
    padding: '8px 16px 12px',
  },
  ofOption: {
    display: 'flex',
    flexDirection: 'column',
    padding: '6px 0',
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': { borderBottom: 'none' },
  },
  ofBadge: {
    display: 'inline-block',
    background: '#e8f0fe',
    color: '#1a73e8',
    borderRadius: 4,
    padding: '1px 7px',
    fontSize: '0.75rem',
    fontWeight: 700,
    marginRight: 8,
  },
  ofRef: {
    display: 'inline-block',
    background: '#f1f3f4',
    color: '#3c4043',
    borderRadius: 3,
    padding: '1px 6px',
    fontSize: '0.75rem',
    fontFamily: 'monospace',
    marginRight: 6,
  },
  ofMetal: {
    display: 'inline-block',
    background: '#fff4e5',
    color: '#a15c00',
    borderRadius: 3,
    padding: '1px 6px',
    fontSize: '0.75rem',
    fontWeight: 600,
    marginRight: 6,
  },
  ofDesignation: {
    fontSize: '0.85rem',
    color: '#202124',
  },
  ofMeta: {
    fontSize: '0.75rem',
    color: '#5f6368',
    marginTop: 2,
    marginLeft: 4,
  },
  ofPrice: {
    fontWeight: 700,
    color: '#188038',
    marginRight: 12,
  },
  ignoreOption: {
    color: '#ea4335',
    fontSize: '0.82rem',
    paddingTop: 6,
  },
  fieldRequired: {
    color: '#ea4335',
    fontWeight: 'bold',
  },
  preview: {
    fontSize: '0.75rem',
    color: '#5f6368',
    fontStyle: 'italic',
    maxWidth: 160,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  resolvedRow: {
    '& td': { color: '#34a853' },
  },
  unmatchedRow: {
    '& td': { color: '#ea4335' },
  },
  ignoredRow: {
    '& td': { color: '#9aa0a6', textDecoration: 'line-through' },
  },
  successBanner: {
    textAlign: 'center',
    padding: theme.spacing(6),
  },
  colChip: {
    margin: '2px',
    fontSize: '0.7rem',
    height: 22,
  },
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────

function norm(str) {
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function detectMapping(headers, fields) {
  const result = {};
  const usedIdx = new Set();
  for (const field of fields) {
    let best = '';
    let bestScore = 0;
    headers.forEach((h, idx) => {
      if (usedIdx.has(idx)) return;
      const hn = norm(h);
      let score = 0;
      for (const kw of field.keywords) {
        const kwn = norm(kw);
        if (hn === kwn) { score = Math.max(score, 1000); break; }
        if (hn.startsWith(kwn)) score = Math.max(score, kwn.length * 3);
        else if (hn.includes(kwn)) score = Math.max(score, kwn.length * 2);
        else if (kwn.includes(hn) && hn.length > 2) score = Math.max(score, hn.length);
      }
      if (score > bestScore) { bestScore = score; best = idx; }
    });
    result[field.key] = bestScore > 0 ? best : '';
    if (bestScore > 0) usedIdx.add(best);
  }
  return result;
}

/** Trouve la première ligne ayant au moins 1 cellule non-vide → ligne d'en-têtes */
function findHeaderRow(raw) {
  for (let i = 0; i < raw.length; i++) {
    const nonEmpty = (raw[i] || []).filter((c) => String(c).trim().length > 0);
    if (nonEmpty.length >= 1) return i;
  }
  return 0;
}

function parseRows(sheet, mapping, headerRowIdx = 0) {
  const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
  if (raw.length < headerRowIdx + 2) return [];
  return raw.slice(headerRowIdx + 1)
    .map((row) => {
      const out = {};
      for (const [field, colIdx] of Object.entries(mapping)) {
        out[field] =
          colIdx !== '' && colIdx !== undefined && row[colIdx] !== undefined
            ? String(row[colIdx]).trim()
            : '';
      }
      return out;
    })
    .filter((r) => (r.ref_fournisseur || '').length > 0 || (r.id_fact || '').length > 0);
}

function getWantedMetalKeyword(refFournisseur) {
  const parts = String(refFournisseur || '').trim().toUpperCase().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return null;
  const metal = parts[parts.length - 1];
  const map = {
    J: 'JAUNE',
    Y: 'JAUNE',
    R: 'ROSE',
    W: 'BLANC',
    G: 'BLANC',
  };
  return map[metal] || null;
}

function optionMatchesMetal(option, wantedMetal, metalMap) {
  if (!wantedMetal) return false;
  const lemetal = norm(resolveMetalLabel(option, metalMap));
  const wanted = norm(wantedMetal);
  return lemetal.includes(wanted);
}

function resolveMetalLabel(option, metalMap) {
  const direct = String(option?.lemetal || option?.metal || option?.type_metal || option?.metal_desc || '').trim();
  if (direct) return direct;
  const metalId = String(option?.type_metal_id || option?.type_metal_id?.toString?.() || '').trim();
  return metalMap[metalId] || '';
}

function extractOptionCarat(option) {
  const text = String(option?.designation || '');
  const matchPacked = text.match(/\b([0-9]+)ct([0-9]{2})\b/i);
  if (matchPacked) return parseFloat(`${matchPacked[1]}.${matchPacked[2]}`);
  const matchDecimal = text.match(/([0-9]+(?:[.,][0-9]+)?)\s*ct/i);
  if (matchDecimal) return parseFloat(String(matchDecimal[1]).replace(',', '.'));
  const matchStandalone = text.match(/(?:^|[^0-9])([0-9]+[.,][0-9]{1,2})(?:[^0-9]|$)/);
  if (matchStandalone) return parseFloat(String(matchStandalone[1]).replace(',', '.'));
  return null;
}

function scoreCaratDistance(option, wantedCarat) {
  if (!wantedCarat) return 0;
  const candidateCarat = extractOptionCarat(option);
  if (candidateCarat === null) return 0;
  const diff = Math.abs(candidateCarat - wantedCarat);
  if (diff <= 0.005) return 100;
  if (diff <= 0.015) return 95;
  if (diff <= 0.025) return 90;
  if (diff <= 0.05) return 80;
  if (diff <= 0.10) return 70;
  if (diff <= 0.20) return 60;
  return 0;
}

function rankOptionsForRow(options, refFournisseur, metalMap) {
  const wantedMetal = getWantedMetalKeyword(refFournisseur);
  const wantedCarat = getWantedCarat(refFournisseur);
  const filtered = wantedMetal
    ? options.filter((opt) => optionMatchesMetal(opt, wantedMetal, metalMap))
    : [...options];

  return filtered.sort((a, b) => {
    const metalScoreA = resolveMetalLabel(a, metalMap) ? 1 : 0;
    const metalScoreB = resolveMetalLabel(b, metalMap) ? 1 : 0;
    if (metalScoreB !== metalScoreA) return metalScoreB - metalScoreA;

    const caratScoreA = scoreCaratDistance(a, wantedCarat);
    const caratScoreB = scoreCaratDistance(b, wantedCarat);
    if (caratScoreB !== caratScoreA) return caratScoreB - caratScoreA;

    return Number(b.score || 0) - Number(a.score || 0);
  });
}

function getWantedCarat(refFournisseur) {
  const parts = String(refFournisseur || '').trim().replace(',', '.').split(/\s+/).filter(Boolean);
  const candidates = parts.slice(1);
  for (const part of candidates) {
    if (/^[0-9]+(?:\.[0-9]+)?$/.test(part)) {
      const value = parseFloat(part);
      if (value > 0 && value <= 20) return value;
    }
  }
  return null;
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function ImportClient() {
  const classes = useStyles();

  // État global
  const [step, setStep] = useState(0);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'info' });

  // Step 0
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [importType, setImportType] = useState('vente');
  const [metalMap, setMetalMap] = useState({});
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [fileRowCount, setFileRowCount] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [headerRowIdx, setHeaderRowIdx] = useState(0);

  // Champs dynamiques selon le type
  const FIELDS = importType === 'stock' ? FIELDS_STOCK : FIELDS_VENTE;

  // Step 1
  const [mapping, setMapping] = useState({});

  // Step 2
  const [analysing, setAnalysing] = useState(false);
  const [results, setResults] = useState(null);
  const [resolutions, setResolutions] = useState({});
  const [ignoredUnmatched, setIgnoredUnmatched] = useState(new Set());

  // Step 3
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Recalcule le mapping quand le type change (et qu'un fichier est déjà chargé)
  useEffect(() => {
    if (headers.length > 0 && sheet) {
      const fields = importType === 'stock' ? FIELDS_STOCK : FIELDS_VENTE;
      setMapping(detectMapping(headers, fields));
    }
  }, [importType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Chargement clients ─────────────────────────────────────────────────
  useEffect(() => {
    setClientsLoading(true);
    getClients()
      .then((r) => setClients(r.data))
      .catch(() =>
        setSnack({ open: true, msg: 'Erreur chargement clients', severity: 'error' })
      )
      .finally(() => setClientsLoading(false));
  }, []);

  useEffect(() => {
    fetch(`${apiATC}/general.php?table=type_metal&orderBy=description`)
      .then((res) => res.json())
      .then((rows) => {
        const nextMap = {};
        (Array.isArray(rows) ? rows : Object.values(rows || [])).forEach((row) => {
          const id = String(row.id_type ?? row.id ?? '');
          const label = String(row.description ?? row.nom ?? '').trim();
          if (id && label) nextMap[id] = label;
        });
        setMetalMap(nextMap);
      })
      .catch(() => {
        setMetalMap({});
      });
  }, []);

  // ─── Gestion fichier ────────────────────────────────────────────────────
  const handleFile = useCallback((f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv', 'ods'].includes(ext)) {
      setSnack({
        open: true,
        msg: 'Format non supporté. Utilisez XLSX, XLS, CSV ou ODS.',
        severity: 'error',
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false });
        if (raw.length < 1) {
          setSnack({ open: true, msg: 'Fichier vide.', severity: 'error' });
          return;
        }
        const hIdx = findHeaderRow(raw);
        const hdrs = (raw[hIdx] || []).map((h) => String(h).trim()).filter((h) => h.length > 0);
        if (hdrs.length === 0) {
          setSnack({ open: true, msg: 'Impossible de détecter les colonnes.', severity: 'error' });
          return;
        }
        setHeaderRowIdx(hIdx);
        setHeaders(hdrs);
        setSheet(ws);
        setFileRowCount(raw.length - 1 - hIdx);
        setFile(f);
        const fields = importType === 'stock' ? FIELDS_STOCK : FIELDS_VENTE;
        setMapping(detectMapping(hdrs, fields));
      } catch {
        setSnack({ open: true, msg: 'Erreur lecture du fichier.', severity: 'error' });
      }
    };
    reader.readAsArrayBuffer(f);
  }, [importType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      handleFile(e.dataTransfer.files[0]);
    },
    [handleFile]
  );

  // ─── Analyse ────────────────────────────────────────────────────────────
  const handleAnalyse = useCallback(async () => {
    setAnalysing(true);
    setStep(2);
    const rows = parseRows(sheet, mapping, headerRowIdx);
    try {
      const data = await analyseImport({
        clientId: selectedClient.id,
        type: importType,
        rows,
        mapping,
      });
      setResults(data);
      const initRes = {};
      (data.ambiguous || []).forEach((row, i) => {
        const ranked = rankOptionsForRow(row?.options || [], row?.ref_fournisseur, metalMap);
        const wantedCarat = getWantedCarat(row?.ref_fournisseur);
        const top = ranked[0];
        const second = ranked[1];
        const topCaratScore = top ? scoreCaratDistance(top, wantedCarat) : 0;
        const secondCaratScore = second ? scoreCaratDistance(second, wantedCarat) : -1;
        const topRankScore = top ? Number(top.score || 0) : 0;
        const secondRankScore = second ? Number(second.score || 0) : -1;
        const isClearlyBest = top && (!second || topCaratScore > secondCaratScore || (topCaratScore === secondCaratScore && topRankScore > secondRankScore));
        initRes[i] = isClearlyBest ? String(top.of_id) : '';
      });
      setResolutions(initRes);
      setIgnoredUnmatched(new Set());
    } catch (err) {
      setSnack({
        open: true,
        msg: err?.message || "Erreur lors de l'analyse.",
        severity: 'error',
      });
      setStep(1);
    } finally {
      setAnalysing(false);
    }
  }, [sheet, mapping, headerRowIdx, selectedClient, importType, metalMap]);

  // ─── Import final ────────────────────────────────────────────────────────
  const handleImport = useCallback(async () => {
    setImporting(true);
    const resolved = [
      ...(results.matched || []),
      ...(results.ambiguous || [])
        .map((row, i) => {
          const choice = resolutions[i];
          if (!choice || choice === 'ignore') return null;
          return { ...row, id_of: choice };
        })
        .filter(Boolean),
    ];
    try {
      const data = await resolveImport({
        clientId: selectedClient.id,
        type: importType,
        resolved,
      });
      setImportResult(data);
      setStep(3);
    } catch (err) {
      setSnack({
        open: true,
        msg: err?.message || "Erreur lors de l'import.",
        severity: 'error',
      });
    } finally {
      setImporting(false);
    }
  }, [results, resolutions, selectedClient, importType]);

  // ─── Dérivés ─────────────────────────────────────────────────────────────
  const ambiguousCount = results?.ambiguous?.length || 0;
  const resolvedCount = Object.values(resolutions).filter((v) => v !== '').length;
  const canImport =
    !analysing && results && (ambiguousCount === 0 || resolvedCount === ambiguousCount);
  const requiredMapped = FIELDS.filter((f) => f.required).every(
    (f) => mapping[f.key] !== '' && mapping[f.key] !== undefined
  );
  const resolvedLinesCount =
    (results?.matched?.length || 0) +
    Object.values(resolutions).filter((v) => v && v !== 'ignore').length;

  const handleReset = () => {
    setStep(0);
    setFile(null);
    setHeaders([]);
    setSheet(null);
    setFileRowCount(0);
    setMapping({});
    setResults(null);
    setResolutions({});
    setIgnoredUnmatched(new Set());
    setImportResult(null);
    setSelectedClient(null);
  };

  const sampleValue = (fieldKey) => {
    const idx = mapping[fieldKey];
    if (idx === '' || idx === undefined || !sheet) return '—';
    const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
    const row = raw[headerRowIdx + 1];
    if (!row) return '—';
    return String(row[idx] ?? '—').slice(0, 40);
  };

  // ══════════════════════════════════════════════════════════════════════════
  // STEP 0 — Configuration
  // ══════════════════════════════════════════════════════════════════════════
  const renderStep0 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Autocomplete
          options={clients}
          loading={clientsLoading}
          getOptionLabel={(o) => `${o.nom} (${o.id})`}
          value={selectedClient}
          onChange={(_, v) => setSelectedClient(v)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client"
              variant="outlined"
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {clientsLoading && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={3}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel>Type d'import</InputLabel>
          <Select
            value={importType}
            onChange={(e) => setImportType(e.target.value)}
            label="Type d'import"
          >
            <MenuItem value="vente">Ventes</MenuItem>
            <MenuItem value="stock">Stock</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <Box
          className={`${classes.dropZone} ${dragOver ? 'active' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input-import').click()}
        >
          <input
            id="file-input-import"
            type="file"
            accept=".xlsx,.xls,.csv,.ods"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files[0])}
          />
          <CloudUploadIcon style={{ fontSize: 52, color: '#1a73e8', marginBottom: 8 }} />
          <Typography variant="body1">
            {file ? (
              <strong>{file.name}</strong>
            ) : (
              'Glissez un fichier XLSX / XLS / CSV ici, ou cliquez'
            )}
          </Typography>
          {file && (
            <Typography variant="body2" style={{ color: '#34a853', marginTop: 6 }}>
              ✓ {fileRowCount} lignes détectées — {headers.length} colonnes
            </Typography>
          )}
        </Box>
      </Grid>

      <Grid item xs={12} className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedClient || !file}
          onClick={() => setStep(1)}
        >
          Suivant →
        </Button>
      </Grid>
    </Grid>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // STEP 1 — Mapping colonnes
  // ══════════════════════════════════════════════════════════════════════════
  const renderStep1 = () => (
    <>
      <Box mb={2}>
        <Typography variant="body2" style={{ marginBottom: 6 }}>
          Colonnes trouvées dans le fichier :
        </Typography>
        {headers.map((h, i) => (
          <Chip key={i} label={h} size="small" className={classes.colChip} variant="outlined" />
        ))}
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Champ ATC</TableCell>
              <TableCell>Requis</TableCell>
              <TableCell style={{ minWidth: 220 }}>Colonne du fichier</TableCell>
              <TableCell>Aperçu 1ère ligne</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {FIELDS.map((field) => (
              <TableRow key={field.key}>
                <TableCell>
                  <strong>{field.label}</strong>
                </TableCell>
                <TableCell>
                  {field.required ? (
                    <span className={classes.fieldRequired}>*</span>
                  ) : (
                    <span style={{ color: '#9aa0a6' }}>optionnel</span>
                  )}
                </TableCell>
                <TableCell>
                  <FormControl size="small" style={{ minWidth: 210 }}>
                    <Select
                      value={
                        mapping[field.key] !== undefined ? mapping[field.key] : ''
                      }
                      onChange={(e) =>
                        setMapping((m) => ({ ...m, [field.key]: e.target.value }))
                      }
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>— Non assigné —</em>
                      </MenuItem>
                      {headers.map((h, idx) => (
                        <MenuItem key={idx} value={idx}>
                          {h}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell className={classes.preview}>{sampleValue(field.key)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!requiredMapped && (
        <Alert severity="warning" style={{ marginTop: 12 }}>
          Les champs obligatoires (<strong>N° Facture</strong> et{' '}
          <strong>Référence fournisseur</strong>) doivent être assignés avant de continuer.
        </Alert>
      )}

      <Box className={classes.actions}>
        <Button onClick={() => setStep(0)}>← Retour</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!requiredMapped}
          onClick={handleAnalyse}
        >
          Analyser ({fileRowCount} lignes)
        </Button>
      </Box>
    </>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // STEP 2 — Analyse & Résolution
  // ══════════════════════════════════════════════════════════════════════════
  const renderStep2 = () => {
    if (analysing) {
      return (
        <Box textAlign="center" py={8}>
          <CircularProgress size={52} />
          <Typography variant="h6" style={{ marginTop: 16 }}>
            Analyse en cours…
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Matching des lignes avec la base de données ATC
          </Typography>
        </Box>
      );
    }
    if (!results) return null;

    const { matched = [], ambiguous = [], unmatched = [], duplicates = [] } = results;

    return (
      <>
        {/* Résumé */}
        <Box mb={3}>
          <Chip
            icon={<CheckCircleOutlineIcon />}
            label={`${matched.length} matchées`}
            className={`${classes.chip} ${classes.chipSuccess}`}
          />
          <Chip
            icon={<WarningIcon />}
            label={`${ambiguous.length} ambiguïté${ambiguous.length > 1 ? 's' : ''}`}
            className={`${classes.chip} ${classes.chipWarning}`}
          />
          <Chip
            icon={<BlockIcon />}
            label={`${unmatched.length} non matchée${unmatched.length > 1 ? 's' : ''}`}
            className={`${classes.chip} ${classes.chipError}`}
          />
          <Chip
            icon={<InfoIcon />}
            label={`${duplicates.length} doublon${duplicates.length > 1 ? 's' : ''} ignoré${duplicates.length > 1 ? 's' : ''}`}
            className={`${classes.chip} ${classes.chipInfo}`}
          />
        </Box>

        {/* ── Ambiguïtés ── */}
        {ambiguous.length > 0 && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              ⚠ Ambiguïtés à résoudre ({resolvedCount}/{ambiguousCount})
            </Typography>
            {ambiguous.map((row, i) => {
              const isResolved = resolutions[i] !== '';
              return (
                <Box
                  key={i}
                  className={`${classes.ambiguityCard} ${isResolved ? classes.ambiguityCardResolved : ''}`}
                >
                  {/* En-tête : infos ligne XLSX */}
                  <Box className={`${classes.ambiguityHeader} ${isResolved ? classes.ambiguityHeaderResolved : ''}`}>
                    <Box style={{ flex: '1 1 100%' }}>
                      {row.libelle && (
                        <Typography variant="body2" className={classes.ambiguityLibelle}>
                          {row.libelle}
                        </Typography>
                      )}
                    </Box>
                    {importType === 'vente' && row.id_fact && (
                      <Typography variant="body2" style={{ fontSize: '0.82rem' }}>
                        <strong>N° Facture :</strong>&nbsp;{row.id_fact}
                      </Typography>
                    )}
                    {row.ref_fournisseur && (
                      <Typography variant="body2" style={{ fontSize: '0.82rem' }}>
                        <strong>Réf. fournisseur :</strong>&nbsp;{row.ref_fournisseur}
                      </Typography>
                    )}
                    <Typography variant="body2" style={{ fontSize: '0.82rem' }}>
                      <strong>Date :</strong>&nbsp;{row.date || '—'}
                    </Typography>
                    {row.prix_vente != null && row.prix_vente !== '' && (
                      <Typography variant="body2" style={{ fontSize: '0.82rem' }}>
                        <strong>Prix :</strong>&nbsp;
                        <span style={{ color: '#188038', fontWeight: 700 }}>
                          {parseFloat(row.prix_vente).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                        </span>
                      </Typography>
                    )}
                  </Box>

                  {/* Corps : choix OF */}
                  <Box className={classes.ambiguityBody}>
                    <RadioGroup
                      value={resolutions[i] || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setResolutions((r) => ({ ...r, [i]: value }));
                      }}
                    >
                      {rankOptionsForRow(row.options || [], row.ref_fournisseur, metalMap).map((opt, j) => (
                        <FormControlLabel
                          key={j}
                          value={String(opt.of_id)}
                          control={<Radio size="small" color="primary" style={{ paddingTop: 4, paddingBottom: 4 }} />}
                          style={{ alignItems: 'flex-start', marginBottom: 2 }}
                          label={
                            <Box className={classes.ofOption}>
                              {/* Ligne 1 : badge OF + ref_cv + designation */}
                              <Box style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0 6px' }}>
                                <span className={classes.ofBadge}>OF #{opt.of_id}</span>
                                {opt.ref_cv && <span className={classes.ofRef}>{opt.ref_cv}</span>}
                                {resolveMetalLabel(opt, metalMap) && <span className={classes.ofMetal}>{resolveMetalLabel(opt, metalMap)}</span>}
                                <span className={classes.ofDesignation}>{opt.designation}</span>
                              </Box>
                              {/* Ligne 2 : date livraison + prix HT + score */}
                              <Box className={classes.ofMeta}>
                                {opt.date_livr && opt.date_livr !== '0000-00-00' && (
                                  <span style={{ marginRight: 12 }}>livr.&nbsp;{opt.date_livr}</span>
                                )}
                                {opt.totht != null && opt.totht !== '' && parseFloat(opt.totht) !== 0 && (
                                  <span className={classes.ofPrice}>
                                    {parseFloat(opt.totht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € HT
                                  </span>
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      ))}
                      <FormControlLabel
                        value="ignore"
                        control={<Radio size="small" style={{ paddingTop: 3, paddingBottom: 3 }} />}
                        label={<span className={classes.ignoreOption}>Ignorer cette ligne</span>}
                      />
                    </RadioGroup>
                  </Box>
                </Box>
              );
            })}
          </>
        )}

        {/* ── Non matchées ── */}
        {unmatched.length > 0 && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              ✗ Lignes non matchées
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {importType === 'vente' && <TableCell>N° Facture</TableCell>}
                    <TableCell>Réf fournisseur</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Motif</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unmatched.map((row, i) => (
                    <TableRow
                      key={i}
                      className={
                        ignoredUnmatched.has(i) ? classes.ignoredRow : classes.unmatchedRow
                      }
                    >
                      {importType === 'vente' && <TableCell>{row.id_fact}</TableCell>}
                      <TableCell>{row.ref_fournisseur}</TableCell>
                      <TableCell>{row.date || '—'}</TableCell>
                      <TableCell>{row.motif || 'Aucune correspondance'}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant={ignoredUnmatched.has(i) ? 'outlined' : 'contained'}
                          color={ignoredUnmatched.has(i) ? 'default' : 'secondary'}
                          onClick={() =>
                            setIgnoredUnmatched((s) => {
                              const ns = new Set(s);
                              ns.has(i) ? ns.delete(i) : ns.add(i);
                              return ns;
                            })
                          }
                        >
                          {ignoredUnmatched.has(i) ? 'Rétablir' : 'Ignorer'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* ── Matchées (aperçu) ── */}
        {matched.length > 0 && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              ✓ Lignes matchées ({matched.length})
            </Typography>
            <TableContainer
              component={Paper}
              variant="outlined"
              style={{ maxHeight: 220, overflow: 'auto' }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {importType === 'vente' && <TableCell>N° Facture</TableCell>}
                    <TableCell>Réf fournisseur</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>OF</TableCell>
                    <TableCell>Désignation</TableCell>
                    <TableCell>Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matched.slice(0, 50).map((row, i) => (
                    <TableRow key={i} className={classes.resolvedRow}>
                      {importType === 'vente' && <TableCell>{row.id_fact}</TableCell>}
                      <TableCell>{row.ref_fournisseur}</TableCell>
                      <TableCell>{row.date || '—'}</TableCell>
                      <TableCell>{row.id_of}</TableCell>
                      <TableCell>{row.designation}</TableCell>
                      <TableCell>{row.score}</TableCell>
                    </TableRow>
                  ))}
                  {matched.length > 50 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" style={{ color: '#9aa0a6' }}>
                        … et {matched.length - 50} lignes supplémentaires
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* ── Doublons ── */}
        {duplicates.length > 0 && (
          <Alert severity="info" style={{ marginTop: 12 }}>
            {duplicates.length} doublon(s) détecté(s) — déjà présents en base, ils seront ignorés.
          </Alert>
        )}

        <Box className={classes.actions}>
          <Button onClick={() => setStep(1)}>← Retour</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!canImport || importing}
            onClick={handleImport}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {importing
              ? 'Import en cours…'
              : `Importer (${resolvedLinesCount} ligne${resolvedLinesCount > 1 ? 's' : ''})`}
          </Button>
        </Box>
      </>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // STEP 3 — Résultat
  // ══════════════════════════════════════════════════════════════════════════
  const renderStep3 = () => (
    <Box className={classes.successBanner}>
      <CheckCircleOutlineIcon style={{ fontSize: 80, color: '#34a853' }} />
      <Typography variant="h5" style={{ marginTop: 16, marginBottom: 8 }}>
        Import terminé !
      </Typography>
      {importResult && (
        <Box mt={1} mb={4}>
          <Chip
            icon={<CheckCircleOutlineIcon />}
            label={`${importResult.inserted} ligne(s) insérée(s)`}
            className={`${classes.chip} ${classes.chipSuccess}`}
          />
          {importResult.duplicates > 0 && (
            <Chip
              icon={<InfoIcon />}
              label={`${importResult.duplicates} doublon(s) ignoré(s)`}
              className={`${classes.chip} ${classes.chipInfo}`}
            />
          )}
          {importResult.ignored > 0 && (
            <Chip
              icon={<BlockIcon />}
              label={`${importResult.ignored} ligne(s) ignorée(s)`}
              className={`${classes.chip} ${classes.chipError}`}
            />
          )}

          {Array.isArray(importResult.errors) && importResult.errors.length > 0 && (
            <Alert severity="warning" style={{ marginTop: 12, textAlign: 'left' }}>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>
                {importResult.errors.length} ligne(s) non insérée(s)
              </Typography>
              <div style={{ maxHeight: 180, overflow: 'auto' }}>
                {importResult.errors.map((err, idx) => (
                  <Typography key={idx} variant="body2" style={{ fontSize: '0.82rem' }}>
                    • {err}
                  </Typography>
                ))}
              </div>
            </Alert>
          )}
        </Box>
      )}
      <Button variant="contained" color="primary" onClick={handleReset}>
        Nouvel import
      </Button>
    </Box>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <Paper className={classes.root} elevation={2}>
      <Typography variant="h5" gutterBottom style={{ fontWeight: 400, marginBottom: 24 }}>
        Import données client
      </Typography>

      <Stepper activeStep={step} className={classes.stepper} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {step === 0 && renderStep0()}
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
